import React, { useState } from 'react'
import Button from 'uiComponents/buttons'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { Modal, ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import {
    DEFAULT_TOKEN_EXPIRY_SECONDS,
    NewServiceApp,
    SERVICE_APP_CAPABILITIES,
    TOKEN_EXPIRY_OPTIONS,
} from '../../schema'
import './createServiceAppModal.scss'

interface CreateServiceAppModalProps {
    accountSlug: string
    saving: boolean
    onCancel: () => void
    onCreate: (app: NewServiceApp) => void
}

export const CreateServiceAppModal: React.FC<CreateServiceAppModalProps> = ({
    accountSlug,
    saving,
    onCancel,
    onCreate,
}) => {
    const [label, setLabel] = useState('')
    const [expiry, setExpiry] = useState(String(DEFAULT_TOKEN_EXPIRY_SECONDS))

    const canCreate = !!label.trim() && !saving

    const submit = () =>
        onCreate({
            label: label.trim(),
            accounts: [accountSlug],
            permissions: { [accountSlug]: SERVICE_APP_CAPABILITIES },
            tokenExpirySeconds: Number(expiry),
        })

    return (
        <Modal title="New service app" onDismiss={saving ? undefined : onCancel} className="create-service-app-modal">
            <ModalBody>
                <p className="create-service-app-intro">
                    A service app lets one of your own tools sign in to Convious on its own, without a person typing a
                    password. It can only ever do what you can do.
                </p>

                <div className="create-service-app-field">
                    <label className="create-service-app-label" htmlFor="service-app-label">
                        App name
                    </label>
                    <input
                        id="service-app-label"
                        className="create-service-app-input"
                        type="text"
                        value={label}
                        maxLength={200}
                        placeholder="For example: nightly sales export"
                        onChange={(e) => setLabel(e.target.value)}
                    />
                    <span className="create-service-app-hint">
                        Only you see this name. It helps you tell your apps apart.
                    </span>
                </div>

                <div className="create-service-app-field">
                    <span className="create-service-app-label">What may it do?</span>
                    <div className="create-service-app-capabilities">
                        {SERVICE_APP_CAPABILITIES.map((capability) => (
                            <span className="create-service-app-capability" key={capability}>
                                {capability}
                            </span>
                        ))}
                    </div>
                    <span className="create-service-app-hint">
                        This is the only thing service apps can do for now, so there is nothing to choose yet.
                    </span>
                </div>

                <div className="create-service-app-field">
                    <label className="create-service-app-label" htmlFor="service-app-expiry">
                        How long may it stay signed in?
                    </label>
                    <select
                        id="service-app-expiry"
                        className="create-service-app-input"
                        value={expiry}
                        onChange={(e) => setExpiry(e.target.value)}
                    >
                        {TOKEN_EXPIRY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <span className="create-service-app-hint">
                        How long a sign-in lasts before the app has to sign in again with its secret. Shorter is safer.
                    </span>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button kind="default" secondary onClick={onCancel} disabled={saving}>
                    Cancel
                </Button>
                <DisabledItemHoverInfotip
                    active={!canCreate}
                    infotipText={
                        saving ? 'Creating your app…' : 'Give the app a name first, so you can recognise it later.'
                    }
                    width="18rem"
                    toTheLeft
                >
                    <Button kind="action" disabled={!canCreate} onClick={submit}>
                        {saving ? 'Creating…' : 'Create'}
                    </Button>
                </DisabledItemHoverInfotip>
            </ModalFooter>
        </Modal>
    )
}
